import { useSearchParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Upload, Trash2, RotateCcw, Save, X, Plus, Pencil, Image as ImageIcon, ArrowUp, ArrowDown, Building2, Camera } from "lucide-react";
import { api, BACKEND_URL as BACKEND_ORIGIN } from "@/lib/api";

const IMAGE_SLOTS = [
  { key: "hero", label: "Homepage Hero / Banner", capMB: 5, aspect: "aspect-[4/5]", desktopAspect: "aspect-[16/9]" },
  { key: "service_loading_unloading", label: "Service — Loading & Unloading", capMB: 2, aspect: "aspect-[4/3]", desktopAspect: "aspect-[4/3]" },
  { key: "service_daily_labour", label: "Service — Daily Labour", capMB: 2, aspect: "aspect-[4/3]", desktopAspect: "aspect-[4/3]" },
  { key: "service_tiles_work", label: "Service — Tiles Work", capMB: 2, aspect: "aspect-[4/3]", desktopAspect: "aspect-[4/3]" },
  { key: "service_house_repair", label: "Service — House Repair / Renovation", capMB: 2, aspect: "aspect-[4/3]", desktopAspect: "aspect-[4/3]" },
];

const abs = (u) => (u ? `${BACKEND_ORIGIN}${u}` : null);

export default function CompanyBranding() {
  const [params, setParams] = useSearchParams();
  const tab = ["profile", "images", "leadership"].includes(params.get("tab")) ? params.get("tab") : "profile";
  const setTab = (t) => setParams(t === "profile" ? {} : { tab: t });
  return (
    <div data-testid="company-branding-page">
      <h1 className="font-display text-2xl font-bold text-slate-100">Company Profile & Branding</h1>
      <p className="text-sm text-slate-400 mt-1 mb-5">Company-wide details, website imagery and leadership profiles. Everything here appears on the public website.</p>
      <div className="flex gap-2 mb-5">
        {[["profile", "Profile"], ["images", "Website Images"], ["leadership", "Leadership & Board"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${tab === k ? "bg-orange-500 text-white border-orange-500" : "bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "profile" && <ProfileTab />}
      {tab === "images" && <ImagesTab />}
      {tab === "leadership" && <LeadershipTab />}
    </div>
  );
}

/* ---------------- Profile ---------------- */
function ProfileTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [preview, setPreview] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(`/admin/company`); setForm(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/company`, {
        name: form.name, phone: form.phone, email: form.email,
        tagline: form.tagline, address: form.address, website: form.website,
      });
      setForm(data); toast.success("Company profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/company/logo`, fd);
      setForm(data); toast.success("Logo updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Logo upload failed"); }
    finally { setLogoUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try { const { data } = await api.delete(`/admin/company/logo`); setForm(data); toast.success("Logo removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  if (!form) return <div className="text-slate-400 flex items-center gap-2 p-6"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="profile-tab">
      <Card className="admin-card p-6 space-y-4 min-w-0">
        <Field label="Company / brand name" testid="cp-name"><Input value={form.name || ""} onChange={(e) => upd("name", e.target.value)} data-testid="cp-name-input" className="bg-slate-900 border-slate-700"/></Field>
        <Field label="Official contact number" testid="cp-phone"><Input value={form.phone || ""} onChange={(e) => upd("phone", e.target.value)} placeholder="+919636873585" data-testid="cp-phone-input" className="bg-slate-900 border-slate-700"/></Field>
        <Field label="Support email" testid="cp-email"><Input value={form.email || ""} onChange={(e) => upd("email", e.target.value)} data-testid="cp-email-input" className="bg-slate-900 border-slate-700"/></Field>
        <Field label="Tagline" testid="cp-tagline"><Input value={form.tagline || ""} onChange={(e) => upd("tagline", e.target.value)} data-testid="cp-tagline-input" className="bg-slate-900 border-slate-700"/></Field>
        <Field label="Company address" testid="cp-address"><Textarea rows={2} value={form.address || ""} onChange={(e) => upd("address", e.target.value)} data-testid="cp-address-input" className="bg-slate-900 border-slate-700"/></Field>
        <Field label="Website URL" testid="cp-website"><Input value={form.website || ""} onChange={(e) => upd("website", e.target.value)} placeholder="https://karmivopvt.ltd" data-testid="cp-website-input" className="bg-slate-900 border-slate-700"/></Field>
        <div className="flex gap-2 pt-2">
          <Button className="bg-orange-500 hover:bg-orange-600" onClick={save} disabled={saving} data-testid="cp-save-btn">
            {saving ? <Loader2 className="animate-spin mr-2" size={14}/> : <Save size={14} className="mr-2"/>} Save
          </Button>
          <Button variant="outline" onClick={load} data-testid="cp-cancel-btn"><X size={14} className="mr-1"/>Cancel</Button>
          <Button variant="outline" onClick={() => setPreview(true)} data-testid="cp-preview-btn"><Building2 size={14} className="mr-1"/>Preview</Button>
        </div>
      </Card>

      <Card className="admin-card p-6 min-w-0" data-testid="logo-card">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Company Logo</div>
        <div className="w-40 h-40 rounded-xl border-2 border-dashed border-slate-600 bg-slate-900 flex items-center justify-center overflow-hidden">
          {form.logo_url
            ? <img src={abs(form.logo_url)} alt="Company logo" className="w-full h-full object-contain" data-testid="logo-preview"/>
            : <ImageIcon className="text-slate-600" size={32}/>}
        </div>
        <div className="flex gap-2 mt-4">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-file-input"/>
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600" disabled={logoUploading} onClick={() => fileRef.current?.click()} data-testid="logo-upload-btn">
            {logoUploading ? <Loader2 className="animate-spin mr-2" size={14}/> : <Upload size={14} className="mr-2"/>} {form.logo_url ? "Replace" : "Upload"}
          </Button>
          {form.logo_url && <Button size="sm" variant="outline" onClick={removeLogo} data-testid="logo-remove-btn"><Trash2 size={14} className="mr-1"/>Remove</Button>}
        </div>
        <p className="text-xs text-slate-500 mt-3">PNG, JPG or WEBP up to 2 MB.</p>
      </Card>

      {preview && (
        <Dialog open onOpenChange={() => setPreview(false)}>
          <DialogContent className="max-w-lg" data-testid="cp-preview-dialog">
            <DialogHeader><DialogTitle>Public preview</DialogTitle></DialogHeader>
            <div className="bg-slate-950 text-slate-300 rounded-lg p-5 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                {form.logo_url ? <img src={abs(form.logo_url)} alt="logo" className="h-8 object-contain"/> : <div className="w-7 h-7 rounded-md bg-orange-500 text-white flex items-center justify-center text-xs font-bold">{(form.name || "K")[0]}</div>}
                <span className="font-display font-bold text-white">{(form.name || "KARMIVO").toUpperCase()}</span>
              </div>
              {form.tagline && <div className="text-slate-400">{form.tagline}</div>}
              <div className="text-xs space-y-1 pt-2 border-t border-slate-800">
                {form.phone && <div>Support: {form.phone}</div>}
                {form.email && <div>{form.email}</div>}
                {form.website && <div>{form.website}</div>}
                {form.address && <div>{form.address}</div>}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ---------------- Images ---------------- */
function ImagesTab() {
  const [images, setImages] = useState(null);
  const [busy, setBusy] = useState({});
  const refs = useRef({});

  const load = async () => {
    try { const { data } = await api.get(`/admin/images`); setImages(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load images"); }
  };
  useEffect(() => { load(); }, []);

  const run = async (slot, fn) => {
    setBusy((b) => ({ ...b, [slot]: true }));
    try { await fn(); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy((b) => ({ ...b, [slot]: false })); }
  };

  if (!images) return <div className="text-slate-400 flex items-center gap-2 p-6"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  return (
    <div className="space-y-5" data-testid="images-tab">
      {IMAGE_SLOTS.map(({ key, label, capMB, aspect, desktopAspect }) => {
        const img = images[key] || {};
        return (
          <Card key={key} className="admin-card p-5" data-testid={`image-slot-${key}`}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between flex-wrap gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-100">{label}</div>
                <div className="text-xs text-slate-500 mt-0.5">PNG/JPG/WEBP, up to {capMB} MB</div>
                <div className="flex gap-6 mt-4 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Mobile crop</div>
                    <div className={`w-28 ${aspect} rounded-lg overflow-hidden border border-slate-700 bg-slate-900 flex items-center justify-center`} data-testid={`${key}-mobile-preview`}>
                      {img.url ? <img src={abs(img.url)} alt={img.alt || label} className="w-full h-full object-cover"/> : <ImageIcon className="text-slate-700" size={20}/>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Desktop crop</div>
                    <div className={`w-48 ${desktopAspect} rounded-lg overflow-hidden border border-slate-700 bg-slate-900 flex items-center justify-center`} data-testid={`${key}-desktop-preview`}>
                      {img.url ? <img src={abs(img.url)} alt={img.alt || label} className="w-full h-full object-cover"/> : <ImageIcon className="text-slate-700" size={20}/>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="shrink-0 w-full md:w-64 space-y-3">
                <div>
                  <Label className="text-slate-400">Alt text</Label>
                  <Input defaultValue={img.alt || ""} key={img.alt || "empty"} onBlur={(e) => { if (e.target.value !== (img.alt || "")) run(key, () => api.patch(`/admin/images/${key}`, { alt: e.target.value })); }} data-testid={`${key}-alt-input`} className="bg-slate-900 border-slate-700 mt-1"/>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" ref={(el) => (refs.current[key] = el)}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) { const fd = new FormData(); fd.append("file", f); fd.append("alt", img.alt || ""); run(key, () => api.post(`/admin/images/${key}`, fd)); } e.target.value = ""; }}
                    data-testid={`${key}-file-input`}/>
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600" disabled={busy[key]} onClick={() => refs.current[key]?.click()} data-testid={`${key}-upload-btn`}>
                    {busy[key] ? <Loader2 className="animate-spin mr-1" size={13}/> : <Upload size={13} className="mr-1"/>} {img.url ? "Replace" : "Upload"}
                  </Button>
                  {img.previous_url && (
                    <Button size="sm" variant="outline" disabled={busy[key]} onClick={() => run(key, () => api.patch(`/admin/images/${key}`, { restore_previous: true }))} data-testid={`${key}-restore-btn`}>
                      <RotateCcw size={13} className="mr-1"/>Restore previous
                    </Button>
                  )}
                  {img.url && (
                    <Button size="sm" variant="outline" disabled={busy[key]} onClick={() => run(key, () => api.delete(`/admin/images/${key}`))} data-testid={`${key}-remove-btn`}>
                      <Trash2 size={13} className="mr-1"/>Remove
                    </Button>
                  )}
                </div>
                {img.updated_at && <div className="text-[11px] text-slate-500">Updated {new Date(img.updated_at).toLocaleString()}</div>}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

/* ---------------- Leadership ---------------- */
const EMPTY_PROFILE = { name: "", designation: "", short_bio: "", experience: "", display_order: 0, published: false };

function LeadershipTab() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [draft, setDraft] = useState(EMPTY_PROFILE);
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);
  const [photoFor, setPhotoFor] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/admin/leadership`); setItems(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load leadership"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft({ ...EMPTY_PROFILE, display_order: (items || []).length }); setEditing("new"); };
  const openEdit = (p) => { setDraft({ ...p }); setEditing(p.id); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post(`/admin/leadership`, draft);
        toast.success("Profile created");
      } else {
        await api.put(`/admin/leadership/${editing}`, draft);
        toast.success("Profile updated");
      }
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/leadership/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const togglePublish = async (p) => {
    try { await api.put(`/admin/leadership/${p.id}`, { published: !p.published }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); }
  };

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await api.post(`/admin/leadership/reorder`, { order: arr.map((p) => p.id) }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reorder failed"); }
  };

  const uploadPhoto = async (id, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/leadership/${id}/photo`, fd);
      toast.success("Photo updated"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Photo upload failed"); }
  };

  if (items === null) return <div className="text-slate-400 flex items-center gap-2 p-6"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  return (
    <div data-testid="leadership-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-400">Only profiles marked <span className="text-emerald-400 font-semibold">Published</span> appear on the public website.</p>
        <Button className="bg-orange-500 hover:bg-orange-600" onClick={openNew} data-testid="leadership-add-btn"><Plus size={14} className="mr-1"/>Add Profile</Button>
      </div>

      {items.length === 0 ? (
        <Card className="admin-card p-10 text-center text-slate-400" data-testid="leadership-empty">
          No leadership profiles yet. Add the first genuine profile when details are available — nothing is invented automatically.
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((p, idx) => (
            <Card key={p.id} className="admin-card p-4 flex items-start gap-4" data-testid={`leadership-row-${p.id}`}>
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-900 border border-slate-700 flex items-center justify-center shrink-0">
                {p.photo_url ? <img src={abs(p.photo_url)} alt={p.name} className="w-full h-full object-cover"/> : <Camera className="text-slate-600" size={20}/>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-100" data-testid="leadership-name">{p.name}</span>
                  <span className="text-xs text-slate-400">{p.designation}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${p.published ? "bg-emerald-900/40 text-emerald-300 border-emerald-700" : "bg-slate-800 text-slate-400 border-slate-600"}`} data-testid="leadership-published-pill">
                    {p.published ? "Published" : "Unpublished"}
                  </span>
                </div>
                {p.short_bio && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{p.short_bio}</p>}
              </div>
              <div className="flex flex-col items-center gap-1 shrink-0">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-white disabled:opacity-30" data-testid={`move-up-${p.id}`}><ArrowUp size={14}/></button>
                <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="text-slate-400 hover:text-white disabled:opacity-30" data-testid={`move-down-${p.id}`}><ArrowDown size={14}/></button>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}><Pencil size={13} className="mr-1"/>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => { setPhotoFor(p.id); setTimeout(() => photoRef.current?.click(), 0); }} data-testid={`photo-${p.id}`}><Camera size={13} className="mr-1"/>Photo</Button>
                <Button size="sm" variant={p.published ? "outline" : "default"} className={p.published ? "" : "bg-emerald-600 hover:bg-emerald-700"} onClick={() => togglePublish(p)} data-testid={`publish-${p.id}`}>
                  {p.published ? "Unpublish" : "Publish"}
                </Button>
                <Button size="sm" variant="outline" className="text-rose-400 border-rose-700 hover:bg-rose-900/30" onClick={() => remove(p.id)} data-testid={`delete-${p.id}`}><Trash2 size={13} className="mr-1"/>Remove</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <input ref={photoRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f && photoFor) uploadPhoto(photoFor, f); e.target.value = ""; }} data-testid="leadership-photo-input"/>

      {editing && (
        <Dialog open onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-lg" data-testid="leadership-editor">
            <DialogHeader><DialogTitle>{editing === "new" ? "Add Profile" : "Edit Profile"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Full name</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="lp-name"/></div>
              <div><Label>Official designation</Label><Input value={draft.designation} onChange={(e) => setDraft({ ...draft, designation: e.target.value })} placeholder="Director / Managing Director / Board Member" data-testid="lp-designation"/></div>
              <div><Label>Short biography</Label><Textarea rows={3} value={draft.short_bio} onChange={(e) => setDraft({ ...draft, short_bio: e.target.value })} data-testid="lp-bio"/></div>
              <div><Label>Professional experience (optional)</Label><Textarea rows={2} value={draft.experience} onChange={(e) => setDraft({ ...draft, experience: e.target.value })} data-testid="lp-experience"/></div>
              <div className="flex items-center justify-between">
                <div className="w-28"><Label>Display order</Label><Input type="number" value={draft.display_order} onChange={(e) => setDraft({ ...draft, display_order: parseInt(e.target.value || "0") })} data-testid="lp-order"/></div>
                <div className="flex items-center gap-2 pt-4"><Switch checked={draft.published} onCheckedChange={(v) => setDraft({ ...draft, published: v })} data-testid="lp-published"/><Label>Published</Label></div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} data-testid="lp-cancel">Cancel</Button>
                <Button className="bg-orange-500 hover:bg-orange-600" onClick={save} disabled={saving} data-testid="lp-save">
                  {saving ? <Loader2 className="animate-spin mr-2" size={14}/> : <Save size={14} className="mr-1"/>} Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const Field = ({ label, testid, children }) => (
  <div data-testid={testid}>
    <Label className="text-slate-400">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
